export const VARIANT_QUERY = `query DuepaProductOffer($id:ID!,$after:String,$country:CountryCode!) @inContext(country:$country) {
  product(id:$id) { id handle requiresSellingPlan variantsCount { count }
    variants(first:100,after:$after) {
      nodes { id title availableForSale requiresShipping requiresComponents
        price { amount currencyCode } compareAtPrice { amount currencyCode }
        selectedOptions { name value } product { id }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
}`;

export function decimalUnits(amount) {
  if (typeof amount !== 'string' || !/^\d+(\.\d{1,6})?$/.test(amount)) throw new Error('Invalid money');
  const [whole, fraction = ''] = amount.split('.');
  return BigInt(whole) * 1000000n + BigInt(fraction.padEnd(6, '0'));
}

function variantNumber(id) {
  if (!/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(id)) throw new Error('Invalid variant ID');
  return BigInt(id.split('/').pop());
}

export function formatMoney(amount, formatter) {
  const units = decimalUnits(amount);
  const digits = formatter.resolvedOptions().maximumFractionDigits;
  if (!Number.isSafeInteger(Number(units)) || digits > 6 || units % (10n ** BigInt(6 - digits)) !== 0n) throw new Error('Money exceeds currency display precision');
  return formatter.format(Number(amount));
}

export function chooseVariant(product, variants, currency, productUrl) {
  if (product.requiresSellingPlan !== false) throw new Error('Selling-plan context required');
  let chosen = null;
  for (const variant of variants) {
    variantNumber(variant.id);
    if (variant.product?.id !== product.id) throw new Error('Product identity mismatch');
    if (variant.price?.currencyCode !== currency) throw new Error('Currency mismatch');
    const price = decimalUnits(variant.price.amount);
    if (variant.compareAtPrice) {
      if (variant.compareAtPrice.currencyCode !== currency) throw new Error('Compare-at currency mismatch');
      decimalUnits(variant.compareAtPrice.amount);
    }
    if (typeof variant.availableForSale !== 'boolean' || typeof variant.requiresShipping !== 'boolean') throw new Error('Missing availability or shipping facts');
    if (typeof variant.title !== 'string' || !Array.isArray(variant.selectedOptions) || !variant.selectedOptions.length ||
        variant.selectedOptions.some(option => typeof option.name !== 'string' || typeof option.value !== 'string')) throw new Error('Missing variant options');
    if (!variant.availableForSale) continue;
    if (variant.requiresComponents !== false) throw new Error('Bundle context required');
    if (!chosen || price < decimalUnits(chosen.price.amount) ||
        (price === decimalUnits(chosen.price.amount) && variantNumber(variant.id) < variantNumber(chosen.id))) chosen = variant;
  }
  if (!chosen) return null;
  const id = chosen.id.split('/').pop();
  if (typeof productUrl !== 'string' || !productUrl.startsWith('/') || productUrl.startsWith('//')) throw new Error('Invalid product URL');
  const url = new URL(productUrl, 'https://store.invalid');
  if (url.origin !== 'https://store.invalid' || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product URL');
  url.searchParams.delete('option_values');
  url.searchParams.set('variant', id);
  const compareAt = chosen.compareAtPrice && decimalUnits(chosen.compareAtPrice.amount) > decimalUnits(chosen.price.amount) ? chosen.compareAtPrice : null;
  return { id, title: chosen.title, options: chosen.selectedOptions, price: chosen.price,
    compareAt, currency, available: true, requiresShipping: chosen.requiresShipping,
    url: url.pathname + url.search + url.hash };
}

export async function resolveOffer({ productId, count, country, currency, productUrl, fetcher = fetch, signal }) {
  const id = `gid://shopify/Product/${productId}`;
  if (!/^\d+$/.test(String(productId)) || !Number.isInteger(count) || count < 1 || count > 2048 || !/^[A-Z]{2}$/.test(country) || !/^[A-Z]{3}$/.test(currency)) throw new Error('Invalid product context');
  const variants = [], ids = new Set(), cursors = new Set();
  let after = null, initial = null;
  for (let page = 0; page < 21; page += 1) {
    const response = await fetcher('/api/2026-10/graphql.json', { method: 'POST', cache: 'no-store', credentials: 'same-origin', signal,
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: VARIANT_QUERY, variables: { id, country, after } }) });
    if (!response.ok) throw new Error(`Storefront HTTP ${response.status}`);
    const body = await response.json();
    const product = body.data?.product;
    if (body.errors?.length || !product || product.id !== id || product.variantsCount?.count !== count) throw new Error('Incomplete product response');
    if (body.extensions?.context?.country !== country) throw new Error('Market context mismatch');
    if (initial && (initial.handle !== product.handle || initial.requiresSellingPlan !== product.requiresSellingPlan)) throw new Error('Product changed during pagination');
    initial = product;
    const connection = product.variants;
    if (!Array.isArray(connection?.nodes) || !connection.pageInfo || typeof connection.pageInfo.hasNextPage !== 'boolean') throw new Error('Missing pagination');
    for (const variant of connection.nodes) {
      if (ids.has(variant.id)) throw new Error('Duplicate variant page');
      ids.add(variant.id); variants.push(variant);
    }
    if (variants.length > count) throw new Error('Variant count changed');
    if (!connection.pageInfo.hasNextPage) {
      if (variants.length !== count) throw new Error('Truncated variant set');
      return { tuple: chooseVariant(product, variants, currency, productUrl), count: variants.length, pages: page + 1 };
    }
    after = connection.pageInfo.endCursor;
    if (!connection.nodes.length || !after || cursors.has(after)) throw new Error('Invalid pagination cursor');
    cursors.add(after);
  }
  throw new Error('Pagination limit exceeded');
}

const inFlight = new Map();
function sharedOffer(context) {
  const key = JSON.stringify([context.productId, context.count, context.country, context.currency, context.productUrl]);
  if (!inFlight.has(key)) {
    const request = resolveOffer({ ...context, signal: AbortSignal.timeout(15000) }).finally(() => inFlight.delete(key));
    inFlight.set(key, request);
  }
  return inFlight.get(key);
}

if (typeof customElements !== 'undefined' && !customElements.get('duepa-v41-product-card')) {
  customElements.define('duepa-v41-product-card', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.purchaseHeld === 'true') { this.dataset.resolverState = 'purchase-held'; return; }
      if (this.dataset.requiresSellingPlan === 'true') { this.dataset.resolverState = 'selling-plan-required'; return; }
      if (Number(this.dataset.variantCount) <= 250) {
        const offer = this.querySelector('[data-v41-offer]');
        const price = this.querySelector('[data-v41-price]');
        const amount = this.querySelector('[data-v41-amount]');
        const link = this.querySelector('[data-v41-product-link]');
        const id = this.dataset.nativeVariantId;
        const valid = this.dataset.nativeTupleValid === 'true' && /^\d+$/.test(id || '') &&
          offer?.dataset.variantId === id && /^\d+$/.test(offer?.dataset.nativePriceCents || '') &&
          offer?.dataset.currency === this.dataset.currency && /^[A-Z]{3}$/.test(this.dataset.currency || '') &&
          price && !price.hidden && amount?.textContent.trim() && link &&
          new URL(link.getAttribute('href'), 'https://store.invalid').searchParams.get('variant') === id;
        const complete = valid && this.dataset.minimumProven === 'true' &&
          Number(this.dataset.nativeScannedCount) === Number(this.dataset.variantCount);
        if (!complete) this.dataset.minimumProven = 'false';
        this.dataset.resolverState = complete ? 'native-complete' : valid ? 'native-fallback' : 'native-unavailable';
        return;
      }
      if (this.dataset.publicContext !== 'true') { this.dataset.resolverState = 'buyer-context-required'; return; }
      this.resolve();
    }
    async resolve() {
      const context = { productId: this.dataset.productId, count: Number(this.dataset.variantCount), country: this.dataset.country,
        currency: this.dataset.currency, productUrl: this.dataset.productUrl };
      const attributes = ['productId', 'variantCount', 'country', 'currency', 'productUrl', 'locale', 'publicContext', 'purchaseHeld', 'requiresSellingPlan'];
      const snapshot = attributes.map(key => this.dataset[key]);
      const generation = this.generation = (this.generation || 0) + 1;
      this.dataset.resolverState = 'loading';
      try {
        const result = await sharedOffer(context);
        if (!this.isConnected || generation !== this.generation) return;
        if (attributes.some((key, index) => this.dataset[key] !== snapshot[index]) || this.dataset.publicContext !== 'true' ||
            this.dataset.purchaseHeld === 'true' || this.dataset.requiresSellingPlan === 'true' ||
            (globalThis.Shopify?.currency?.active && globalThis.Shopify.currency.active !== context.currency) ||
            (globalThis.Shopify?.country && globalThis.Shopify.country !== context.country)) throw new Error('Buyer or product context changed during resolution');
        const tuple = result.tuple;
        const price = this.querySelector('[data-v41-price]');
        const link = this.querySelector('[data-v41-product-link]');
        const offer = this.querySelector('[data-v41-offer]');
        const soldOut = this.querySelector('[data-v41-sold-out]');
        if (!price || !link || !offer || !soldOut) throw new Error('Offer markup missing');
        if (tuple) {
          const amountNode = this.querySelector('[data-v41-amount]');
          const comparisonNode = this.querySelector('[data-v41-compare-amount]');
          const comparisonWrap = this.querySelector('[data-v41-compare]');
          const prefix = this.querySelector('[data-v41-prefix]');
          const from = this.querySelector('[data-v41-from]');
          const title = this.querySelector('[data-v41-variant-title]');
          if (!amountNode || !comparisonNode || !comparisonWrap || !prefix || !from || !title) throw new Error('Offer markup missing');
          const format = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || undefined, { style: 'currency', currency: tuple.currency, currencyDisplay: 'code' });
          const amount = formatMoney(tuple.price.amount, format);
          const comparison = tuple.compareAt ? formatMoney(tuple.compareAt.amount, format) : '';
          amountNode.textContent = amount;
          comparisonNode.textContent = comparison;
          comparisonWrap.hidden = !tuple.compareAt;
          prefix.textContent = from.textContent;
          title.textContent = tuple.title;
          link.setAttribute('href', tuple.url);
          offer.dataset.variantId = tuple.id;
          offer.dataset.currency = tuple.currency;
          offer.dataset.resolvedPrice = tuple.price.amount;
          offer.dataset.resolvedCompareAt = tuple.compareAt?.amount || '';
          offer.dataset.available = 'true';
          offer.dataset.requiresShipping = String(tuple.requiresShipping);
          offer.dataset.selectedOptions = JSON.stringify(tuple.options);
          price.hidden = false; soldOut.hidden = true;
        } else {
          price.hidden = true; soldOut.hidden = false;
          link.setAttribute('href', context.productUrl);
          offer.dataset.variantId = '';
          offer.dataset.resolvedPrice = '';
          offer.dataset.resolvedCompareAt = '';
          offer.dataset.selectedOptions = '';
          offer.dataset.available = 'false';
          offer.dataset.requiresShipping = '';
        }
        this.dataset.minimumProven = 'true';
        this.dataset.resolverState = 'complete';
        this.dataset.resolvedVariantCount = String(result.count);
        this.dispatchEvent(new CustomEvent('duepa:v41:offer', { bubbles: true, detail: result }));
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        this.dataset.resolverState = 'error';
        this.dispatchEvent(new CustomEvent('duepa:v41:offer-error', { bubbles: true, detail: { productId: context.productId, reason: error.message } }));
      }
    }
    disconnectedCallback() { this.generation = (this.generation || 0) + 1; }
  });
}
